// src/pages/markets/api/usePositions.ts
//
// Paginated, server-sorted, server-filtered positions for one trader.
// Now supports:
//   - full page-based pagination (page number + pageSize) instead of load-more
//   - per-column sort direction (asc | desc) toggled by the table header
//   - total row count returned by the backend for page-count calculation

import { useState, useEffect, useRef, useCallback } from 'react';
import { apiGet } from './apiClient';
import type {
    TraderPosition,
    PositionsPage,
    PositionCategory,
} from './traderProfileTypes';
import { normalizePositionRow } from './normalizeTraderProfile';

export type PositionSort =
    | 'open_position'
    | 'total_pnl'
    | 'realized_pnl'
    | 'unrealized_pnl'
    | 'current_value'
    | 'recent';

export const POSITION_SORT_DEFAULT_DIR: Record<PositionSort, SortDir> = {
    open_position: 'desc',
    total_pnl: 'desc',
    realized_pnl: 'desc',
    unrealized_pnl: 'desc',
    current_value: 'desc',
    recent: 'desc',
};

export type SortDir = 'asc' | 'desc';

export interface UsePositionsOpts {
    sort: PositionSort;
    sortDir: SortDir;
    page: number; // 0-based
    pageSize: number;
    eventId?: string | null;
    side?: 'YES' | 'NO' | null;
    category?: PositionCategory | null;
    heldFrom?: string | null;
    heldTo?: string | null;
}

export interface UsePositionsResult {
    positions: TraderPosition[];
    loading: boolean;
    error: string | null;
    total: number; // total rows matching filters (from backend count)
    pageCount: number; // Math.ceil(total / pageSize)
    reload: () => void;
}

export function usePositions(
    address: string | undefined,
    opts: UsePositionsOpts
): UsePositionsResult {
    const {
        sort,
        sortDir,
        page,
        pageSize,
        eventId = null,
        side = null,
        category = null,
        heldFrom = null,
        heldTo = null,
    } = opts;

    const [positions, setPositions] = useState<TraderPosition[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const callIdRef = useRef(0);
    const abortRef = useRef<AbortController | null>(null);

    const fetchPage = useCallback(() => {
        if (!address) return;

        const callId = ++callIdRef.current;
        setLoading(true);
        setError(null);

        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        const offset = page * pageSize;
        const params = new URLSearchParams({
            sort,
            order: sortDir,
            limit: String(pageSize),
            offset: String(offset),
        });
        if (eventId) params.set('event_id', eventId);
        if (side) params.set('side', side);
        if (category) params.set('category', category);
        if (heldFrom) params.set('held_from', heldFrom);
        if (heldTo) params.set('held_to', heldTo);

        apiGet<PositionsPage>(
            `/api/trader/${encodeURIComponent(address)}/positions?${params}`,
            controller.signal
        )
            .then(res => {
                if (callId !== callIdRef.current) return;
                const rows = (res.positions ?? []).map(normalizePositionRow);
                setPositions(rows);
                setTotal(res.total ?? 0);
            })
            .catch(err => {
                if (err?.name === 'AbortError' || callId !== callIdRef.current)
                    return;
                console.error('positions fetch failed:', err);
                setError(err instanceof Error ? err.message : String(err));
                setPositions([]);
                setTotal(0);
            })
            .finally(() => {
                if (callId !== callIdRef.current) return;
                setLoading(false);
            });
    }, [
        address,
        sort,
        sortDir,
        page,
        pageSize,
        eventId,
        side,
        category,
        heldFrom,
        heldTo,
    ]);

    useEffect(() => {
        fetchPage();
    }, [fetchPage]);

    useEffect(() => () => abortRef.current?.abort(), []);

    const reload = useCallback(() => {
        fetchPage();
    }, [fetchPage]);

    const pageCount = pageSize > 0 ? Math.ceil(total / pageSize) : 0;

    return { positions, loading, error, total, pageCount, reload };
}
